import { MouseEvent, useState } from 'react';
import {Ellipsis} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@shortsworks/ui/components/ui/dropdown-menu';
import {Button} from '@shortsworks/ui/components/ui/button';
import {cn} from '@shortsworks/ui/lib/utils';

const SIDE_OPTIONS = ["top", "right", "bottom", "left"];
const ALIGN_OPTIONS = ["start", "center", "end"];
type Side = (typeof SIDE_OPTIONS)[number];
type Align = (typeof ALIGN_OPTIONS)[number];

export type MoreMenuHandler<T = any, U = any> = (type: string, value: U, data: T) => void;

export interface MoreMenuItem {
  title: string | ((data: any) => string);
  value?: any;
  type: string;
  icon?: any;
  children?: MoreMenuItem[];
}

export interface MoreMenuProps {
  data?: any;
  items: MoreMenuItem[];
  onClickMenu?: MoreMenuHandler;
  side?: Side;
  align?: Align;
  sideOffset?: number;
  alignOffset?: number;
}

export function MoreMenu({ data, onClickMenu, items = [], side, sideOffset, align, alignOffset }: MoreMenuProps) {
  const [ opened, setOpened ] = useState<boolean>(false);

  const handleClickTrigger = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setOpened(true);
  }

  const handleMenuItemClick = (e: MouseEvent, item: MoreMenuItem) => {
    e.preventDefault();
    e.stopPropagation();

    onClickMenu?.(item.type, item.value, data);
    setOpened(false);
  }

  const handleOpenChange = (open: boolean) => {
    setOpened(open);
  }

  const getAlign = () => {
    if (!side) return {};

    if (side === 'top' || side === 'bottom') {
      switch (align) {
        case 'start':
          return {left: alignOffset ?? 0};
        case 'center':
          return {left: '50%', transform: 'translateX(-50%)'};
        case 'end':
          return {right: alignOffset ?? 0};
      }
    } else {
      switch (align) {
        case 'start':
          return {top: alignOffset ?? 0};
        case 'center':
          return {top: '50%', transform: 'translateY(-50%)'};
        case 'end':
          return {bottom: alignOffset ?? 0};
      }
    }
  }

  return (
    <DropdownMenu open={opened} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger onClick={handleClickTrigger} asChild>
        <Button
          variant="ghost"
          className={cn(
            'p-0 w-5 h-3 rounded-lg bg-white border border-input',
            !side ? 'relative' : 'absolute',
          )}
          style={{
            ...(side === 'right' && { right: sideOffset ?? 0 }),
            ...(side === 'left' && { left: sideOffset ?? 0 }),
            ...(side === 'top' && { top: sideOffset ?? 0 }),
            ...(side === 'bottom' && { bottom: sideOffset ?? 0 }),
            ...(getAlign()),
          }}
        >
          <Ellipsis className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="start">
        {
          items.map((item: MoreMenuItem, index: number) => {
            if (item.children && item.children.length > 0) {
              return (
                <DropdownMenuSub key={index}>
                  <DropdownMenuSubTrigger>
                    {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                    {typeof item.title === 'string' ? item.title : item.title(data)}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {
                        item.children?.map((childItem, childIndex) => (
                          <DropdownMenuItem key={childIndex} onClick={(e: MouseEvent) => handleMenuItemClick(e, childItem)}>
                            {childItem?.icon && <childItem.icon className="mr-2 h-4 w-4" />}
                            {typeof childItem?.title === 'string' ? childItem?.title : childItem?.title(data)}
                          </DropdownMenuItem>
                        ))
                      }
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )
            }

            return (
              <DropdownMenuItem key={index} onClick={(e: MouseEvent) => handleMenuItemClick(e, item)}>
                {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                {typeof item.title === 'string' ? item.title : item.title(data)}
              </DropdownMenuItem>
            )
          })
        }
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
